import React, { Component } from 'react';
import T from 'prop-types';
// recompose
import { compose } from 'recompose';
import withCorrection from 'containers/DailyCheckup/utils/withCorrectionHOC';
// redux
import { connect } from 'react-redux';
import { reportWaterUsage } from 'reducers/dailyCheckup/checkupEdit';
import { setPageOptions } from 'reducers/layout';
import { openModal } from 'reducers/modals';
// components
import { FormattedMessage, FormattedNumber } from 'react-intl';
import Link from 'react-router/lib/Link';
import Input from 'components/Input';
import MessageBox from 'components/MessageBox';
import Button from 'components/Button';
import PigsUnderCareLine from 'components/PigsUnderCareLine/PigsUnderCareLine';
import CenterBox from 'components/CenterBox';
import CommentBox from 'components/CommentBox';
import StickyHeader from 'components/StickyHeader/StickyHeader';
import StickyFooter from 'components/StickyFooter/StickyFooter';
import ReactSelect from 'components/Select/ReactSelect';
import WaterMeterRolloverModal from 'components/DailyCheckups/modals/WaterMeterRolloverModal/WaterMeterRolloverModal';
import penIcon from '../../../public/images/pen.svg';
import DailyCheckupEditProtector from './DailyCheckupEditProtector';
// utils
import { toastResponseErrors } from 'utils/responseErrorsHelper';
import { animateScroll } from 'react-scroll';
import { sendNotify } from 'utils/airbrakeHelper';
import { calculateMgPerHead, formatWater } from 'utils';
import { getCommentByRole } from 'helpers.js';
// styles
import './DailyCheckupReportWaterUsage.scss';
// constants
import { comment_roles } from 'constants.js';

class DailyCheckupReportWaterUsage extends Component {

  constructor(props) {
    super(props);
    const { measurementSystem } = props;
    this.state = this.getInitialValues(props.checkup);
    this.timeOptions = measurementSystem === 'imperial' ?
      [{ label: 'AM', value: 'am' }, { label: 'PM', value: 'pm' }] :
      [{ label: '24-h', value: '24h' }];
  }

  componentDidMount() {
    const { checkup, params, setPageOptions, isEditMode, backLink } = this.props;
    if (checkup.id === parseInt(params.id, 10)) {
      setPageOptions({
        backLink: { icon: 'fa fa-times fs-18', link: backLink },
        mobileTitle: 'waterUsage',
        inEditMode: isEditMode
      });
    }
  }

  componentDidUpdate(prevProps) {
    const { setPageOptions, checkup, backLink, isEditMode } = this.props;
    if (prevProps.checkup !== checkup) {
      this.setState({
        ...this.getInitialValues(checkup)
      });
      setPageOptions({
        backLink: { icon: 'fa fa-times fs-18', link: backLink },
        mobileTitle: 'waterUsage',
        inEditMode: isEditMode
      });
    }
  }

  componentWillUnmount() {
    this.props.setPageOptions({ mobileTitle: '' });
  }

  getInitialValues = (checkup) => {
    const { measurementSystem } = this.props;
    const { comment, mentioned_user_ids } = getCommentByRole(checkup?.comments || [], comment_roles.WATER_USAGE_ROLE);

    let timeFormat;
    let hours;
    let minutes;
    if (checkup.water_usage_recorded_at) {
      const timeInCurrentFormat = this.convertTimeFormat(checkup.water_usage_recorded_at, measurementSystem);
      timeFormat = measurementSystem === 'imperial' && checkup.water_usage_recorded_at >= '12:00' ? 'pm' : 'am';
      if (measurementSystem !== 'imperial') {
        timeFormat = '24h';
      }
      [hours, minutes] = timeInCurrentFormat.split(':');
    } else {
      const date = new Date();
      hours = date.getHours();
      minutes = date.getMinutes();
      const timeInCurrentFormat = this.convertTimeFormat(`${hours}:${minutes}`, measurementSystem);
      timeFormat = measurementSystem === 'imperial' && hours >= 12 ? 'pm' : 'am';
      if (measurementSystem !== 'imperial') {
        timeFormat = '24h';
      }
      [hours, minutes] = timeInCurrentFormat.split(':');
    }

    const isStandardMultiMeter = this.isStandardMultiMeter();
    const meterReadings = {};
    (checkup?.water_meters || []).forEach((meter) => {
      const saved = isStandardMultiMeter ? meter.derived_consumption : meter.meter_reading;
      meterReadings[meter.id] = saved != null ? formatWater(Number(saved), measurementSystem) : '';
    });

    return {
      water_usage_value: formatWater(Number(checkup?.water_usage_value || 0), measurementSystem),
      meter_reading_value: checkup?.water_meter_reading != null
        ? formatWater(Number(checkup.water_meter_reading), measurementSystem)
        : '',
      meterReadings,
      rolloverMeterIds: [],
      comment: comment || '',
      mentionsIds: mentioned_user_ids || [],
      timeFormat,
      hours,
      minutes,
      inputResetKey: 0,
    };
  };

  isRunningMeter = () => {
    const { checkup, isEditMode } = this.props;
    if (isEditMode) return false;
    if (!checkup?.pig_group?.farm?.running_meter_mode) return false;
    // Mid-cycle switch: this DC already carries a Standard-mode entry. Per spec,
    // Running Meter applies to the next NEW checkup — finish today in Standard mode.
    const hasStandardEntry = checkup?.water_usage_value != null && checkup?.water_meter_reading == null;
    if (hasStandardEntry) return false;
    return true;
  };

  getPriorMeterReading = () => this.props.checkup?.prior_meter_reading || null;

  // Server stores meter readings in imperial; convert to caregiver's unit for display.
  getPriorMeterReadingInUserUnit = () => {
    const prior = this.getPriorMeterReading();
    if (!prior) return null;
    const { measurementSystem } = this.props;
    return {
      value: formatWater(Number(prior.value), measurementSystem),
      created_on: prior.created_on,
    };
  };

  isPriorReadingYesterday = () => {
    const prior = this.getPriorMeterReading();
    const checkupDate = this.props.checkup?.created_on;
    if (!prior?.created_on || !checkupDate) return false;
    const priorTime = new Date(prior.created_on).getTime();
    const expectedYesterday = new Date(checkupDate).getTime() - 24 * 60 * 60 * 1000;
    return Math.abs(priorTime - expectedYesterday) < 60 * 60 * 1000;
  };

  getDerivedConsumption = () => {
    const { meter_reading_value } = this.state;
    const priorInUserUnit = this.getPriorMeterReadingInUserUnit();
    if (!priorInUserUnit || !this.isPriorReadingYesterday()) return null;
    if (meter_reading_value === '' || meter_reading_value == null) return null;
    const delta = Number(meter_reading_value) - Number(priorInUserUnit.value);
    return delta >= 0 ? delta : null;
  };

  // Multi-meter mode: the group has a configured registry of named meters.
  isMultiMeter = () => {
    const { checkup, isEditMode } = this.props;
    if (isEditMode) return false;
    if (!checkup?.pig_group?.farm?.running_meter_mode) return false;
    if (!Array.isArray(checkup?.water_meters) || checkup.water_meters.length === 0) return false;
    // Mid-cycle switch: a Standard-mode entry was made before meters were
    // configured — finish today in Standard mode (mirrors single-meter mode).
    const hasMeterReadings = checkup.water_meters.some((meter) => meter.meter_reading != null);
    const hasStandardEntry = checkup.water_usage_value != null
      && checkup.water_meter_reading == null
      && !hasMeterReadings;
    return !hasStandardEntry;
  };

  // Standard mode with named meters: one consumption value per meter, no
  // odometer/baseline logic. Mutually exclusive with isMultiMeter.
  isStandardMultiMeter = () => {
    const { checkup, isEditMode } = this.props;
    if (isEditMode) return false;
    if (checkup?.pig_group?.farm?.running_meter_mode) return false;
    return Array.isArray(checkup?.water_meters) && checkup.water_meters.length > 0;
  };

  hasMeterInputs = () => this.isMultiMeter() || this.isStandardMultiMeter();

  getMeters = () => this.props.checkup?.water_meters || [];

  getMeterEnteredValue = (meter) => {
    const value = this.state.meterReadings[meter.id];
    return value === '' || value == null ? null : Number(value);
  };

  getMeterPriorInUserUnit = (meter) => {
    const prior = meter?.prior_reading;
    if (!prior || prior.value == null) return null;
    const { measurementSystem } = this.props;
    return { value: formatWater(Number(prior.value), measurementSystem), created_on: prior.created_on };
  };

  isMeterPriorYesterday = (meter) => {
    const prior = meter?.prior_reading;
    const checkupDate = this.props.checkup?.created_on;
    if (!prior?.created_on || !checkupDate) return false;
    const priorTime = new Date(prior.created_on).getTime();
    const expectedYesterday = new Date(checkupDate).getTime() - 24 * 60 * 60 * 1000;
    return Math.abs(priorTime - expectedYesterday) < 60 * 60 * 1000;
  };

  getMeterDerived = (meter) => {
    const value = this.state.meterReadings[meter.id];
    const prior = this.getMeterPriorInUserUnit(meter);
    if (!prior || !this.isMeterPriorYesterday(meter)) return null;
    if (value === '' || value == null) return null;
    const delta = Number(value) - Number(prior.value);
    return delta >= 0 ? delta : null;
  };

  // Standard: sum of the entered values. Running: sum of the per-meter deltas;
  // meters without a computable delta contribute 0.
  getMetersTotalConsumption = () => {
    const isStandardMultiMeter = this.isStandardMultiMeter();
    return this.getMeters().reduce((sum, meter) => {
      const value = isStandardMultiMeter ? this.getMeterEnteredValue(meter) : this.getMeterDerived(meter);
      return value != null ? sum + value : sum;
    }, 0);
  };

  getEnteredMetersCount = () => this.getMeters().filter((meter) => this.getMeterEnteredValue(meter) != null).length;

  isMetersIncomplete = () => this.getEnteredMetersCount() < this.getMeters().length;

  handleMeterChange = (meterId) => ({ target: { value } }) => {
    const raw = this.state.meterReadings[meterId];
    const current = String(raw == null ? '' : raw);
    if (!current.includes('.') && (value || '').toString().substr(-1) === '.') {
      this.setState((prev) => ({ meterReadings: { ...prev.meterReadings, [meterId]: value } }));
      return;
    }
    const pattern = /^\d+(\.\d{0,2})?$/; // float number with two decimal point
    if (value === '' || `0${value}`.match(pattern)) {
      this.setState((prev) => ({
        meterReadings: { ...prev.meterReadings, [meterId]: value === '' ? '' : Number(value) },
      }));
    }
  };

  convertTimeFormat = (time, measurementSystem) => {
    if (measurementSystem !== 'imperial') {
      return time;
    }

    const [hours, minutes] = time.split(':');
    const hoursImperial = hours % 12 || 12;

    return `${hoursImperial}:${minutes}`;
  };

  onCommentOpen = () => {
    animateScroll.scrollToBottom({ containerId: 'bodybag' });
  };

  handleInputChange = ({ target: { value } }) => {
    const fieldKey = this.isRunningMeter() ? 'meter_reading_value' : 'water_usage_value';
    const currentValue = this.state[fieldKey];
    if (
      !currentValue.toString().includes('.') &&
      (value || '').toString().substr(-1) === '.'
    ) {
      this.setState({ [fieldKey]: value });
      return;
    }
    const pattern = /^\d+(\.\d{0,2})?$/; // float number with two decimal point
    if (`0${value}`.match(pattern)) {
      this.setState({ [fieldKey]: Number(value) });
    }
  };

  handleEditReadingFromRollover = () => {
    this.setState((prevState) => ({
      meter_reading_value: '',
      inputResetKey: prevState.inputResetKey + 1,
    }));
  };

  handleSaveBaseline = () => {
    this.submitData({ confirmRollover: true });
  };

  handleRolloverResponse = async (errResponse) => {
    try {
      const body = await errResponse;
      if (body && body.error && body.error.code === 'rollover_detected') {
        const { openModal, measurementSystem } = this.props;
        const priorImperial = body.error.prior_reading?.value;
        const todayImperial = body.error.today_reading;
        openModal(
          <WaterMeterRolloverModal
            priorReading={priorImperial != null ? formatWater(Number(priorImperial), measurementSystem) : null}
            priorReadingDate={body.error.prior_reading?.created_on}
            todayReading={todayImperial != null ? formatWater(Number(todayImperial), measurementSystem) : null}
            onEditReading={this.handleEditReadingFromRollover}
            onSaveBaseline={this.handleSaveBaseline}
          />
        );
        return true;
      }
    } catch (e) {
      // not a rollover response — fall through to default error handling
    }
    return false;
  };

  handleMultiRolloverResponse = async (errResponse) => {
    try {
      const body = await errResponse;
      const meters = body?.error?.code === 'rollover_detected' ? body.error.meters : null;
      if (Array.isArray(meters) && meters.length) {
        const { openModal, measurementSystem } = this.props;
        const meter = meters[0];
        const priorImperial = meter.prior_reading?.value;
        const todayImperial = meter.today_reading;
        openModal(
          <WaterMeterRolloverModal
            meterName={meter.name}
            priorReading={priorImperial != null ? formatWater(Number(priorImperial), measurementSystem) : null}
            priorReadingDate={meter.prior_reading?.created_on}
            todayReading={todayImperial != null ? formatWater(Number(todayImperial), measurementSystem) : null}
            onEditReading={() => this.handleEditMeterReading(meter.water_meter_id)}
            onSaveBaseline={() => this.handleSaveMeterBaseline(meter.water_meter_id)}
          />
        );
        return true;
      }
    } catch (e) {
      // not a rollover response — fall through to default error handling
    }
    return false;
  };

  handleEditMeterReading = (meterId) => {
    this.setState((prevState) => ({
      meterReadings: { ...prevState.meterReadings, [meterId]: '' },
      inputResetKey: prevState.inputResetKey + 1,
    }));
  };

  handleSaveMeterBaseline = (meterId) => {
    this.setState(
      (prevState) => ({ rolloverMeterIds: [...new Set([...prevState.rolloverMeterIds, meterId])] }),
      () => this.submitData()
    );
  };

  handleTimeChange = ({ target: { value, name } }) => {

    let result = null;

    if (name === 'hours') {
      result = this.normalizeHours(value);
    }

    if (name === 'minutes') {
      result = this.normalizeMinutes(value);
    }

    this.setState({
      [name]: Number(result)
    });
  };

  normalizeHoursValue = (value) => {
    if (!value) {
      return 0;
    }
    return value.length > 1 ? value.replace(/^0+/, '') : value;
  };

  normalizeMinutesValue = (value) => {
    if (!value) {
      return 0;
    }
    const normalizedValue =  value.length > 2 ? value.replace(/^0+/, '') : value;

    return normalizedValue.padStart(2, '0');
  };

  handleTimeSelect = ({ target: { value } }) => {
    const [hours, minutes] = value.split(':');
    this.setState({
      hours,
      minutes,
    });
  }

  normalizeHours = (value) => {
    const { timeFormat } = this.state;

    if (!value) {
      return 0;
    }

    const time = Number(value.length > 2 ? value.replace(/^0+/, '') : value);


    if (timeFormat === '24h' && time > 24) {
      return 23;
    }

    if (timeFormat === 'pm' && time > 12) {
      return 12;
    }

    if (timeFormat === 'am' && time > 12) {
      return 12;
    }

    return time > 23 ? 23 : time;
  };

  normalizeMinutes = (value) => {
    if (!value) {
      return 0;
    }

    const time = Number(value.length > 2 ? value.replace(/^0+/gi, '') : value);

    return time > 59 ? 59 : time;
  };

  normalizeTime = (hours = 0, minutes = 0) => (
    `${hours < 10 ? `0${hours}` : hours}:${minutes < 10 ? `0${minutes}` : minutes}`
  );

  submitData = (options = {}) => {
    const { confirmRollover = false } = options;
    const { router } = this.context;
    const {
      reportWaterUsage,
      checkup,
      validateCheckup,
      backLink,
      makeCheckupCorrection,
      isEditMode,
      currentUser,
      measurementSystem
    } = this.props;
    const {
      water_usage_value, meter_reading_value, meterReadings, rolloverMeterIds,
      comment, mentionsIds, hours, minutes, timeFormat,
    } = this.state;

    if (!validateCheckup(checkup)) return;

    const isStandardMultiMeter = this.isStandardMultiMeter();
    const isMultiMeter = this.isMultiMeter();
    const isRunningMeter = !isMultiMeter && this.isRunningMeter();
    const resource = {
      comments_water_usage: {
        text: comment,
        mentioned_user_ids: mentionsIds
      },
      water_usage_recorded_at: this.normalizeTime(hours, minutes),
      time_format: timeFormat,
    };

    if (isStandardMultiMeter) {
      resource.water_meter_consumptions = this.getMeters()
        .filter((meter) => this.getMeterEnteredValue(meter) != null)
        .map((meter) => ({
          water_meter_id: meter.id,
          value: formatWater(this.getMeterEnteredValue(meter), 'imperial', measurementSystem),
        }));
    } else if (isMultiMeter) {
      resource.water_meter_readings = this.getMeters()
        .filter((meter) => {
          const value = meterReadings[meter.id];
          return value !== '' && value != null;
        })
        .map((meter) => ({
          water_meter_id: meter.id,
          meter_reading: formatWater(meterReadings[meter.id], 'imperial', measurementSystem),
        }));
      if (rolloverMeterIds.length) resource.confirm_rollover_meter_ids = rolloverMeterIds;
    } else if (isRunningMeter) {
      resource.water_meter_reading = formatWater(meter_reading_value, 'imperial', measurementSystem);
      if (confirmRollover) resource.confirm_rollover = true;
    } else {
      resource.water_usage_value = formatWater(water_usage_value, 'imperial', measurementSystem);
    }

    const submitRequest = isEditMode
      ? makeCheckupCorrection(checkup.id, resource)
      : reportWaterUsage(checkup.id, { resource });

    submitRequest
      .then(() => router.push(backLink))
      .catch(async (response) => {
        if (isMultiMeter) {
          const handled = await this.handleMultiRolloverResponse(response);
          if (handled) return;
        } else if (isRunningMeter) {
          const handled = await this.handleRolloverResponse(response);
          if (handled) return;
        }
        sendNotify(response, currentUser);
        toastResponseErrors(response);
      });
  };

  isContinueDisabled = () => {
    const {
      water_usage_value: currentWaterUsageValue,
      meter_reading_value: currentMeterReading,
      comment,
      hours,
      minutes,
    } = this.state;
    const {
      water_usage_value,
      water_meter_reading,
      comments = [],
      water_usage_recorded_at,
    } = this.props.checkup;

    const commentData = getCommentByRole(comments, comment_roles.WATER_USAGE_ROLE);

    const [savedHours, savedMinutes] = (water_usage_recorded_at || '10:00').split(':');

    const isEqualTime = savedHours === hours && savedMinutes === minutes;

    const savedComment = commentData.comment || '';
    const currentComment = comment || '';
    const isEqualComment = savedComment === currentComment;

    if (this.hasMeterInputs()) {
      const anyEntered = this.getEnteredMetersCount() > 0;
      return !anyEntered && isEqualComment && isEqualTime;
    }

    const isEqualValue = this.isRunningMeter()
      ? (currentMeterReading === '' || +currentMeterReading === +(water_meter_reading || 0))
      : +currentWaterUsageValue === +water_usage_value;

    return isEqualComment && isEqualValue && isEqualTime;
  };

  getWaterUsagePerHead = (checkup, currentValue) => {
    const { initial_pigs, pigs } = checkup;

    if (!initial_pigs && !pigs) return 0;
    return calculateMgPerHead(currentValue, initial_pigs || pigs);
  };

  renderMeterInputs = () => {
    const { meterReadings, inputResetKey } = this.state;
    const isStandardMultiMeter = this.isStandardMultiMeter();

    return this.getMeters().map((meter) => {
      const value = meterReadings[meter.id];
      const prior = isStandardMultiMeter ? null : this.getMeterPriorInUserUnit(meter);
      const derived = isStandardMultiMeter ? null : this.getMeterDerived(meter);

      return (
        <div className="water-meter-block" key={meter.id}>
          <div className="water-usage-row">
            <label className="input-wrapper">
              <span className="input-label">{meter.name}</span>
              <Input
                key={`${meter.id}-${inputResetKey}`}
                type="number"
                placeholder="0"
                pattern="[0-9]*"
                value={value === '' || value == null ? '' : value}
                onChange={this.handleMeterChange(meter.id)}
                className="water-usage-input"
                hasNoMargin
                alignRight
                bolded
              />
            </label>
          </div>
          {derived != null && (
            <div className="water-usage-row meter-derived-preview">
              <FormattedMessage
                id="container.checkupEdit.meterDerivedConsumption"
                values={{
                  consumed: <FormattedNumber value={derived} format="0,0[.]00" />,
                  today: <FormattedNumber value={value} format="0,0[.]00" />,
                  prior: <FormattedNumber value={prior.value} format="0,0[.]00" />,
                }}
              />
            </div>
          )}
          {!isStandardMultiMeter && !prior && (
            <div className="water-usage-row meter-baseline-hint">
              <FormattedMessage id="container.checkupEdit.meterBaselineHint" />
            </div>
          )}
          {prior && !this.isMeterPriorYesterday(meter) && (
            <div className="water-usage-row meter-gap-hint">
              <FormattedMessage
                id="container.checkupEdit.meterGapHint"
                values={{ date: prior.created_on }}
              />
            </div>
          )}
        </div>
      );
    });
  };

  render() {
    const { isReportingWater, checkup, backLink, isEditMode, measurementSystem, hasMentions } = this.props;
    const {
      comment, mentionsIds, water_usage_value, meter_reading_value, timeFormat, hours, minutes, inputResetKey,
    } = this.state;
    const isContinueDisabled = this.isContinueDisabled();
    const hasMeterInputs = this.hasMeterInputs();
    const isRunningMeter = !hasMeterInputs && this.isRunningMeter();
    const priorMeterReading = this.getPriorMeterReadingInUserUnit();
    const derivedConsumption = isRunningMeter ? this.getDerivedConsumption() : null;
    const isMetersIncomplete = hasMeterInputs && this.isMetersIncomplete();
    let perHeadInputValue;
    if (hasMeterInputs) {
      perHeadInputValue = this.getMetersTotalConsumption();
    } else if (!isRunningMeter) {
      perHeadInputValue = water_usage_value;
    } else if (derivedConsumption != null) {
      perHeadInputValue = derivedConsumption;
    } else {
      perHeadInputValue = 0;
    }
    const waterUsagePerHead = this.getWaterUsagePerHead(checkup, perHeadInputValue);
    const limit = measurementSystem === 'imperial' ? 8 : 40; // 8 gallons for imperial, 40 liters for metric
    const isWaterUsageExceeded = waterUsagePerHead > limit;
    const water_unit = measurementSystem === 'imperial' ? 'gallonage' : 'liters';

    return (
      <CenterBox
        className="DailyCheckupReportWaterUsage"
        isLoading={isReportingWater}
        renderIf={Boolean(checkup)}
      >
        <DailyCheckupEditProtector pigGroup={checkup.pig_group}>
          <StickyHeader className="hide-for-large">
            <PigsUnderCareLine mobile pigsCount={checkup.pigs} />
          </StickyHeader>

          <section className="center-box-header show-for-large">
            <div>
              <div className="subtitle">{checkup.pig_group_name}</div>
              <h1 className="title">
                <FormattedMessage id="general.waterUsage" />
              </h1>
            </div>
            <Link to={backLink}>
              <i className="fa fa-times close-center-box" />
            </Link>
          </section>

          <section className="center-box-body">
            {isEditMode && (
              <MessageBox type="warning">
                <strong>
                  <FormattedMessage id="component.alertBox.warning" />
                  &nbsp;
                </strong>
                <FormattedMessage id="container.checkupEdit.editWarning" />
              </MessageBox>
            )}
            {isWaterUsageExceeded && (
              <MessageBox type="alert">
                <span className="water-usage-error">
                  <FormattedMessage id="component.alertBox.error" tagName="strong" />&nbsp;
                  <FormattedMessage id="container.checkupEdit.waterUsagePerHeadExceeded" />
                </span>
              </MessageBox>
            )}
            <div className="water-usage-inputs">
              {hasMeterInputs && this.renderMeterInputs()}
              {isMetersIncomplete && (
                <MessageBox type="warning">
                  <FormattedMessage id="container.checkupEdit.waterMetersIncomplete" />
                </MessageBox>
              )}
              {!hasMeterInputs && (
                <div className="water-usage-row">
                  <label className="input-wrapper">
                    <span className="input-label">
                      <FormattedMessage
                        id={isRunningMeter ? 'general.meterReading' : `general.${water_unit}Consumed`}
                      />
                    </span>
                    <Input
                      key={inputResetKey}
                      type="number"
                      placeholder="0"
                      pattern="[0-9]*"
                      value={isRunningMeter ? (meter_reading_value || '') : (water_usage_value || '')}
                      onChange={this.handleInputChange}
                      autoFocus={isRunningMeter && inputResetKey > 0}
                      className="water-usage-input"
                      hasNoMargin
                      alignRight
                      bolded
                    />
                  </label>
                </div>
              )}
              {isRunningMeter && derivedConsumption != null && (
                <div className="water-usage-row meter-derived-preview">
                  <FormattedMessage
                    id="container.checkupEdit.meterDerivedConsumption"
                    values={{
                      consumed: <FormattedNumber value={derivedConsumption} format="0,0[.]00" />,
                      today: <FormattedNumber value={meter_reading_value} format="0,0[.]00" />,
                      prior: <FormattedNumber value={priorMeterReading.value} format="0,0[.]00" />,
                    }}
                  />
                </div>
              )}
              {isRunningMeter && !priorMeterReading && (
                <div className="water-usage-row meter-baseline-hint">
                  <FormattedMessage id="container.checkupEdit.meterBaselineHint" />
                </div>
              )}
              {isRunningMeter && priorMeterReading && !this.isPriorReadingYesterday() && (
                <div className="water-usage-row meter-gap-hint">
                  <FormattedMessage
                    id="container.checkupEdit.meterGapHint"
                    values={{ date: priorMeterReading.created_on }}
                  />
                </div>
              )}
              <div className="water-usage-row desktop">
                <span className="input-label">
                  <FormattedMessage id="general.timeRecorded" />
                </span>
                <Input
                  name="hours"
                  type="number"
                  placeholder="0"
                  pattern="[0-9]*"
                  value={this.normalizeHoursValue(String(hours))}
                  onChange={this.handleTimeChange}
                  className="water-usage-time"
                  hasNoMargin
                  alignRight
                  bolded
                />
                <span className="colon-separator ">:</span>
                <Input
                  name="minutes"
                  type="number"
                  pattern="[0-9]*"
                  placeholder="0"
                  value={this.normalizeMinutesValue(String(minutes))}
                  onChange={this.handleTimeChange}
                  className="water-usage-time"
                  hasNoMargin
                  alignRight
                  bolded
                />
                <ReactSelect
                  className="time-select"
                  placeholder={
                    <FormattedMessage id="container.dailyCheckup.reportMovements.selectMovementType" />
                }
                  options={this.timeOptions}
                  value={timeFormat}
                  onChange={(option) => {
                    this.setState({ timeFormat: option.value });
                  }}
                  searchable={false}
                  noInputMargin
                />
              </div>
              <div className="water-usage-row mobile">
                <span className="input-label">
                  <FormattedMessage id="general.timeRecorded" />
                </span>
                <Input
                  name="hours"
                  type="time"
                  value={`${hours}:${minutes}`}
                  onChange={this.handleTimeSelect}
                  className="water-usage-time"
                  hasNoMargin
                  alignRight
                  bolded
                />
                <img src={penIcon} alt="Edit Time" />
              </div>
              {hasMeterInputs && (
                <div className="water-usage-row">
                  <label className="input-wrapper">
                    <span className="input-label">
                      <FormattedMessage id={`general.${water_unit}Consumed`} />
                    </span>
                    <Input
                      value={this.getMetersTotalConsumption()}
                      className="water-usage-input"
                      hasNoMargin
                      alignRight
                      bolded
                      disabled
                    />
                  </label>
                </div>
              )}
              <div className="water-usage-row">
                <label className="input-wrapper">
                  <span className="input-label">
                    <FormattedMessage id={`general.${water_unit}PerHead`} />
                  </span>
                  <Input
                    placeholder="n/a"
                    value={isRunningMeter && derivedConsumption == null ? '' : waterUsagePerHead}
                    className="water-usage-input"
                    hasNoMargin
                    alignRight
                    bolded
                    disabled
                  />
                </label>
              </div>
            </div>
            <CommentBox
              hasMentions={hasMentions}
              disabledIds={mentionsIds}
              entityId={checkup?.pig_group?.farm_id}
              entityType="farm"
              onOpen={this.onCommentOpen}
              onValueChange={(comment) => this.setState((prevState) => ({ ...prevState, comment }))}
              onMentionsChange={(mentions) => {
                this.setState((prevState) => ({ ...prevState, mentionsIds: mentions.map((i) => i.id) }));
              }}
              value={comment}
              isOpen={!!comment}
              placeholder=""
              useSpeechToText
            />
          </section>

          <section className="center-box-footer show-for-large">
            <div className="buttons text-right">
              <Link className="button margin-right-1" to={backLink}>
                <FormattedMessage id="general.button.cancel" />
              </Link>
              <Button
                primary
                disabled={isContinueDisabled || isReportingWater || isWaterUsageExceeded}
                className="submit wider"
                onClick={this.submitData}
              >
                <FormattedMessage id="general.button.continue" />
              </Button>
            </div>
          </section>
          <StickyFooter className="hide-for-large" hidden={isContinueDisabled}>
            <Button
              stickyFooter
              primary
              disabled={isContinueDisabled || isReportingWater || isWaterUsageExceeded}
              onClick={this.submitData}
            >
              <FormattedMessage id="general.button.continue" />
            </Button>
          </StickyFooter>
        </DailyCheckupEditProtector>
      </CenterBox>
    );
  }
}

DailyCheckupReportWaterUsage.contextTypes = {
  router: T.object.isRequired
};

DailyCheckupReportWaterUsage.propTypes = {
  checkup: T.object,
  isReportingWater: T.bool.isRequired,
  setPageOptions: T.func.isRequired,
  reportWaterUsage: T.func.isRequired,
  validateCheckup: T.func.isRequired,
  isEditMode: T.bool,
  hasMentions: T.bool,
  params: T.object,
  backLink: T.oneOfType([T.string, T.object]),
  makeCheckupCorrection: T.func,
  currentUser: T.object.isRequired,
  measurementSystem: T.string.isRequired,
  openModal: T.func.isRequired,
};

const enhance = compose(
  connect(
    (state, props) => ({
      currentUser: state.auth.user,
      measurementSystem: state.auth.user.current_company.measurement_system,
      isReportingWater: props.isLoading || state.dailyCheckup.checkupEdit.isReportingWater,
      hasMentions: !props.isEditMode || state.auth.user.current_company.edit_mode_mentions,
    }),
    { reportWaterUsage, setPageOptions, openModal }
  ),
  withCorrection
);

export default enhance(DailyCheckupReportWaterUsage);
